export type ParsedCsv = {
  header: string[];
  rows: string[][];
};

// Minimal RFC4180-ish CSV parser (handles quotes + commas + newlines).
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let i = 0;
  let inQuotes = false;

  // Strip BOM
  if (text.charCodeAt(0) === 0xfeff) {
    text = text.slice(1);
  }

  while (i < text.length) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === "\"") {
        const next = text[i + 1];
        if (next === "\"") {
          cell += "\"";
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      cell += ch;
      i += 1;
      continue;
    }

    if (ch === "\"") {
      inQuotes = true;
      i += 1;
      continue;
    }

    if (ch === ",") {
      row.push(cell);
      cell = "";
      i += 1;
      continue;
    }

    if (ch === "\r") {
      // Handle CRLF
      if (text[i + 1] === "\n") i += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      i += 1;
      continue;
    }

    if (ch === "\n") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      i += 1;
      continue;
    }

    cell += ch;
    i += 1;
  }

  // Last cell/row
  row.push(cell);
  rows.push(row);

  // Drop trailing empty row
  if (rows.length > 0 && rows[rows.length - 1].every((c) => c.trim() === "")) {
    rows.pop();
  }

  return rows;
}

export function parseCsvWithHeader(text: string): ParsedCsv {
  const all = parseCsv(text);
  if (all.length === 0) return { header: [], rows: [] };
  const [header, ...rows] = all;
  const normalizedHeader = header.map((h) => h.trim());
  const filteredRows = rows.filter((r) => r.some((c) => c.trim() !== ""));
  return { header: normalizedHeader, rows: filteredRows };
}

export function getCsvColumnIndex(header: string[], name: string): number {
  const target = name.trim().toLowerCase();
  return header.findIndex((h) => h.trim().toLowerCase() === target);
}

export function escapeCsvCell(value: string): string {
  if (value.includes('"')) {
    value = value.replace(/"/g, '""');
  }
  if (/[",\n\r]/.test(value)) {
    return `"${value}"`;
  }
  return value;
}

export function buildCsvFromRows(header: string[], rows: string[][]): string {
  const lines = [header.map(escapeCsvCell).join(",")];
  for (const r of rows) {
    lines.push(r.map((c) => escapeCsvCell(String(c))).join(","));
  }
  return lines.join("\r\n");
}

export function downloadCsvFile(filename: string, csv: string): void {
  const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

